import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import type { FieldWork, FieldWorkMeta } from '@/api/endpoints';

/**
 * Parsuje datę ISO jako LOKALNĄ północ.
 *
 * `new Date('2026-08-25')` jest interpretowane jako UTC, co w polskiej strefie cofa datę o dzień
 * i przesuwa CAŁY wykres. Konwencja repo (MonthCalendar) — doklejamy godzinę.
 */
function parseIso(iso: string): Date {
  return new Date(iso + 'T00:00:00');
}

const DAY_MS = 86_400_000;

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const MONTHS = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

/** Polska odmiana: 1 praca, 2–4 prace, 5+ prac (z wyjątkiem nastek). */
export function odmianaPrac(n: number): string {
  if (n === 1) return 'praca';
  const dwie = n % 10, dziesiatki = n % 100;
  if (dwie >= 2 && dwie <= 4 && (dziesiatki < 12 || dziesiatki > 14)) return 'prace';
  return 'prac';
}

interface Props {
  works: FieldWork[];
  meta: FieldWorkMeta | null;
  /** Klik w pasek — nawigacja do karty pracy. */
  onSelect?: (workId: number) => void;
}

/**
 * Gantt terminów prac terenowych.
 *
 * Świadomie WŁASNY komponent, nie chart.js: w projekcie nie ma adaptera dat (skala czasowa
 * wysypałaby się w przeglądarce), a Gantt potrzebuje lepkiej kolumny etykiet i pasków o dowolnej
 * długości — pływające słupki chart.js dawałyby to okrężną drogą i gorzej.
 *
 * Pasek rysujemy z `firstDate`–`lastDate`, NIE z `totalDays`: to ostatnie jest sumą dni z wpisów,
 * więc nakładające się terminy liczyłoby podwójnie. Pojedynczy dzień to pasek jednodniowy, nie
 * zerowej szerokości. Osobne wpisy tej samej pracy rysujemy jako oddzielne segmenty na jednym
 * wierszu — arkusz miał tu jedną scaloną kreskę i gubił przerwy między wyjazdami.
 */
export function PraceGantt({ works, meta, onSelect }: Props) {
  const colorOf = (statusId: string) =>
    meta?.statuses.find(s => s.id === statusId)?.mapColor ?? '#6b7280';

  const model = useMemo(() => {
    const rows = works
      .map(w => {
        const segments = w.dates
          .filter(d => d.dateFrom)
          .map(d => ({
            from: parseIso(d.dateFrom as string),
            to: parseIso((d.dateTo ?? d.dateFrom) as string),
            note: d.note,
          }))
          .sort((a, b) => a.from.getTime() - b.from.getTime());
        return { work: w, segments };
      })
      .filter(r => r.segments.length > 0);

    if (rows.length === 0) return null;

    let min = rows[0].segments[0].from;
    let max = rows[0].segments[0].to;
    for (const r of rows) {
      for (const s of r.segments) {
        if (s.from < min) min = s.from;
        if (s.to > max) max = s.to;
      }
      // Kreska terminu umowy też musi się zmieścić w osi, inaczej zniknie poza krawędzią.
      if (r.work.contractDeadline) {
        const dl = parseIso(r.work.contractDeadline);
        if (dl < min) min = dl;
        if (dl > max) max = dl;
      }
    }
    // Margines, żeby skrajne paski nie kleiły się do krawędzi.
    const start = addDays(min, -3);
    const end = addDays(max, 3);
    const totalDays = Math.max(1, daysBetween(start, end) + 1);

    // Etykiety miesięcy — pełne dni byłyby nieczytelne przy zakresie liczonym w latach.
    const ticks: { left: number; label: string }[] = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor <= end) {
      const offset = daysBetween(start, cursor);
      if (offset >= 0) {
        ticks.push({
          left: (offset / totalDays) * 100,
          label: `${MONTHS[cursor.getMonth()]} ${String(cursor.getFullYear()).slice(2)}`,
        });
      }
      cursor.setMonth(cursor.getMonth() + 1);
    }

    const todayOffset = daysBetween(start, new Date(new Date().setHours(0, 0, 0, 0)));
    const todayLeft = todayOffset >= 0 && todayOffset <= totalDays
      ? (todayOffset / totalDays) * 100 : null;

    return { rows, start, totalDays, ticks, todayLeft };
  }, [works]);

  // Prace bez rozpoznanej daty nie mogą trafić na oś. Nie chowamy ich po cichu — licznik niżej.
  const withoutDates = works.length - (model?.rows.length ?? 0);

  if (!model) {
    // Pusty wynik filtrów to co innego niż „prace bez terminów" — mylenie tych dwóch komunikatów
    // wysyła użytkownika na poszukiwanie nieistniejącego problemu z danymi.
    return (
      <div className="p-8 text-center text-ink-400 text-[13px]">
        {works.length === 0
          ? 'Brak prac pasujących do filtrów.'
          : `Żadna z ${works.length} prac nie ma wpisanego terminu wykonania — nie ma czego umieścić na osi czasu.`}
      </div>
    );
  }

  const ROW_H = 30;
  const pct = (d: Date) => (daysBetween(model.start, d) / model.totalDays) * 100;

  return (
    <div>
      {/* Kolumna etykiet stoi poza obszarem przewijania, żeby przy przewijaniu w bok
          nie stracić informacji, czyja to praca. */}
      <div className="flex">
        <div className="shrink-0 w-[260px] border-r border-ink-100">
          <div className="h-[34px] border-b border-ink-100 px-3 flex items-end pb-1 text-[11px] text-ink-400 uppercase tracking-wide">
            Praca
          </div>
          {model.rows.map(r => (
            <div key={r.work.id} style={{ height: ROW_H }}
                 className="px-3 flex flex-col justify-center border-b border-ink-50 hover:bg-ink-50">
              <NavLink to={`/prace/${r.work.id}`}
                       className="text-[12px] text-ink-800 truncate hover:text-water-600 leading-tight">
                <span className="font-mono text-ink-400 mr-1">{r.work.seqNo}</span>
                {r.work.wellLabel ? `${r.work.wellLabel} — ` : ''}{r.work.clientName ?? r.work.title}
              </NavLink>
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-x-auto">
          {/* min-width na elemencie POŚREDNIM: rodzic ma overflow, a siatka musi mieć realną
              szerokość, żeby paski nie zlewały się przy długim zakresie. */}
          <div style={{ minWidth: Math.max(560, model.totalDays * 3) }} className="relative">
            <div className="h-[34px] border-b border-ink-100 relative">
              {model.ticks.map((t, i) => (
                <div key={i} className="absolute top-0 bottom-0 border-l border-ink-100"
                     style={{ left: `${t.left}%` }}>
                  <span className="absolute left-1 bottom-1 text-[10px] text-ink-400 whitespace-nowrap">
                    {t.label}
                  </span>
                </div>
              ))}
            </div>

            <div className="relative">
              {model.ticks.map((t, i) => (
                <div key={i} className="absolute top-0 bottom-0 border-l border-ink-50 pointer-events-none"
                     style={{ left: `${t.left}%` }}/>
              ))}
              {model.todayLeft != null && (
                <div className="absolute top-0 bottom-0 border-l-2 border-water-500 pointer-events-none z-10"
                     style={{ left: `${model.todayLeft}%` }} title="dziś"/>
              )}

              {model.rows.map(r => {
                const deadline = r.work.contractDeadline ? parseIso(r.work.contractDeadline) : null;
                return (
                  <div key={r.work.id} style={{ height: ROW_H }}
                       className="relative border-b border-ink-50 hover:bg-ink-50">
                    {r.segments.map((s, i) => {
                      const left = pct(s.from);
                      // +1 dzień: termin jednodniowy ma być widoczną kreską, nie zerem.
                      const width = ((daysBetween(s.from, s.to) + 1) / model.totalDays) * 100;
                      return (
                        <button key={i} type="button"
                                onClick={() => onSelect?.(r.work.id)}
                                title={`${r.work.title}\n${ymd(s.from)}${
                                  s.to > s.from ? ` – ${ymd(s.to)}` : ''}${s.note ? `\n${s.note}` : ''}`}
                                className="absolute top-1/2 -translate-y-1/2 rounded-sm hover:brightness-110"
                                style={{
                                  left: `${left}%`,
                                  width: `max(3px, ${width}%)`,
                                  height: 14,
                                  background: colorOf(r.work.status),
                                }}/>
                      );
                    })}
                    {deadline && (
                      <div className="absolute top-0 bottom-0 pointer-events-none"
                           style={{ left: `${pct(deadline)}%` }}
                           title={`termin umowy: ${r.work.contractDeadline}`}>
                        <div className="w-0 h-0 border-l-[4px] border-r-[4px] border-t-[6px]
                                        border-l-transparent border-r-transparent border-t-red-500
                                        absolute top-0 -translate-x-1/2"/>
                        <div className="absolute top-0 bottom-0 border-l border-dashed border-red-300 -translate-x-1/2"/>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap px-3 py-2 border-t border-ink-100 text-[11px] text-ink-500">
        {(meta?.statuses ?? []).map(s => (
          <span key={s.id} className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-2 rounded-sm" style={{ background: s.mapColor }}/>
            {s.name}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-0 h-0 border-l-[4px] border-r-[4px] border-t-[6px]
                           border-l-transparent border-r-transparent border-t-red-500"/>
          termin umowy
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-0.5 h-3 bg-water-500"/> dziś
        </span>
        {withoutDates > 0 && (
          <span className="ml-auto text-amber-700">
            {withoutDates} {odmianaPrac(withoutDates)} bez rozpoznanej daty — poza osią
          </span>
        )}
      </div>
    </div>
  );
}
